import { MessageSquare } from "lucide-react";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import { cn } from "@/core/utils/utils";
import type { PayrollStatementSmsDelivery } from "./types";
import { getSmsDeliveryReason, getSmsDeliveryStatusLabel } from "./utils";

/**
 * The text that went with a PayPack email, one quiet line under the email's own
 * status. Only a failure is coloured: a skipped text (no number, replied STOP) is
 * the technician's choice or missing data, not something that went wrong.
 */
export const SmsDeliveryStatus = ({
  sms,
}: {
  sms: PayrollStatementSmsDelivery;
}) => {
  const isFailed = sms.status === "failed";
  const reason = getSmsDeliveryReason(sms);
  const label = `${getSmsDeliveryStatusLabel(sms)}${reason ? ` · ${reason}` : ""}`;

  // Capped so a long provider error truncates instead of widening the table.
  return (
    <Stack
      direction="row"
      align="center"
      gap="xs"
      className="min-w-0 max-w-56"
      title={label}
    >
      <MessageSquare
        className={cn("h-3.5 w-3.5 shrink-0", {
          "text-red-50": isFailed,
          "text-green-60": sms.status === "succeeded",
          "text-tertiary-text": !isFailed && sms.status !== "succeeded",
        })}
      />
      <Typography
        variant="captionW1"
        textColor={isFailed ? "negative" : "tertiary"}
        className="truncate"
      >
        {label}
      </Typography>
    </Stack>
  );
};
